import React, { createContext, useContext, useState, useCallback } from "react";

const translations = {
  de: {
    nav: { home: "Home", services: "Leistungen", gateways: "Global Gateways", investment: "Investment & Immobilien", network: "Netzwerk", about: "Über SBG", contact: "Kontakt", cta: "Vertrauliches Gespräch" },
    hero: {
      kicker: "Salvatore Business Gateway",
      line1: "Ihr Geschäft",
      line2: "über Grenzen",
      line3: "hinaus erweitern.",
      sub: "Wir verbinden Unternehmer, Investoren und Entscheidungsträger, um Chancen zu schaffen, wo andere Barrieren sehen.",
      cta: "Vertrauliches Gespräch beginnen",
      scroll: "Scrollen",
    },
    intro: {
      label: "Unsere Identität",
      heading: "Wir sind kein Beratungsunternehmen. Wir sind ein Gateway.",
      body: "Ein Gateway öffnet Türen. Ein Gateway verbindet Menschen. Ein Gateway schafft Möglichkeiten. Geschäft basiert auf Vertrauen. Wachstum beginnt mit den richtigen Verbindungen.",
    },
    manifesto: {
      label: "Manifest",
      items: [
        { t: "Wir schaffen Zugang.", d: "Wir verkaufen keine Standardberatung. Wir schaffen Chancen und Zugang zu den richtigen Menschen, der richtigen Expertise und dem richtigen Netzwerk." },
        { t: "Wir verbinden.", d: "Wir bringen Unternehmer, Investoren, Experten und internationale Partner zusammen. Unser wahrer Wert liegt in unserem Netzwerk." },
        { t: "Wir koordinieren.", d: "Wir beraten nicht einfach. Wir verbinden, koordinieren und führen aus — diskret und mit internationaler Exzellenz." },
        { t: "Wir verkörpern Vertrauen.", d: "Unser Produkt ist Vertrauen und Vertraulichkeit. Unser Kapital ist unser Netzwerk. Unsere Mission ist es, die richtigen Menschen zusammenzubringen." },
      ],
    },
    marquee: ["Vertrauen", "Zugang", "Netzwerk", "Diskretion", "Chancen", "International"],
    gateways: {
      label: "Global Gateways",
      heading: "Internationale Chancen für Unternehmer.",
      body: "Serbien ist einer unserer ersten strategischen Knotenpunkte. Von dort begleiten wir Unternehmer weltweit — mit weiteren Standorten, die wir kontinuierlich erweitern.",
      featured: "Erster Knotenpunkt",
      upcoming: "In Vorbereitung",
      view: "Alle Gateways ansehen",
    },
    services: {
      label: "Leistungen",
      heading: "Zugang zu einem exklusiven internationalen Netzwerk.",
      body: "Unsere Leistungen sind keine Standardpakete. Sie sind Zugang. Unsere Rolle ist es zu koordinieren, zu verbinden und zu begleiten.",
      list: [
        { t: "Business Relocation", d: "Standortverlagerung und internationale Aufstellung." },
        { t: "Company Formation", d: "Gründung und Strukturierung von Gesellschaften." },
        { t: "Banking & Financial Coordination", d: "Koordination von Banken und Finanzpartnern." },
        { t: "Legal & Tax Coordination", d: "Rechtliche und steuerliche Koordination." },
        { t: "Real Estate", d: "Zugang zu Premium-Immobilien." },
        { t: "Investment Structuring", d: "Strukturierung von Investitionen." },
        { t: "Strategic Partnerships", d: "Strategische Partnerschaften und Verbindungen." },
        { t: "Executive Concierge", d: "Diskreter Service auf höchstem Niveau." },
      ],
    },
    network: {
      label: "Netzwerk",
      heading: "Unser Kapital ist unser Netzwerk.",
      body: "Wir verbinden Menschen. Wir verbinden Ideen. Wir verbinden Kapital. Wir verbinden Chancen. Wenn Sie unser Netzwerk betreten, sprechen Sie mit den Menschen, mit denen Sie sprechen möchten.",
      stats: [
        { n: "Vertraulich", l: "Jedes Gespräch" },
        { n: "International", l: "Ohne Grenzen" },
        { n: "Ausgewählt", l: "Richtige Verbindungen" },
      ],
    },
    about: {
      label: "Über SBG",
      heading: "SBG ist eine internationale Plattform.",
      body: "SBG steht für Salvatore Business Gateway — von Anfang an als internationale Marke konzipiert. Serbien ist einer unserer ersten Knotenpunkte, doch unsere Vision endet nicht an Landesgrenzen.",
      p2: "Wenn Besucher unsere Website verlassen, sollen sie nicht denken: „Interessantes Beratungsunternehmen.“ Sondern: „Das sind Menschen, mit denen ich sprechen möchte.“",
    },
    contact: {
      label: "Kontakt",
      heading: "Beginnen Sie ein vertrauliches Gespräch.",
      body: "Der erste Kontakt soll sich wie der Beginn einer langfristigen Partnerschaft anfühlen — nicht wie der Kauf eines Produkts.",
      name: "Name", email: "E-Mail", company: "Unternehmen", interest: "Interessengebiet", message: "Ihre Nachricht",
      submit: "Gespräch beginnen", sending: "Wird gesendet…",
      success: "Vielen Dank. Wir melden uns diskret bei Ihnen.",
      error: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    },
    investment: {
      label: "Investment & Immobilien",
      heading: "Wo Strategie auf Chancen trifft.",
      body: "Zugang zu Premium-Immobilien und durchdachten Investitionsstrukturen — koordiniert, nicht versprochen.",
    },
    footer: { rights: "Alle Rechte vorbehalten.", tagline: "Business ohne Grenzen. Zugang ohne Barrieren." },
  },
  en: {
    nav: { home: "Home", services: "Services", gateways: "Global Gateways", investment: "Investment & Real Estate", network: "Network", about: "About SBG", contact: "Contact", cta: "Confidential Conversation" },
    hero: {
      kicker: "Salvatore Business Gateway",
      line1: "Expanding Your",
      line2: "Business Beyond",
      line3: "Borders.",
      sub: "We connect entrepreneurs, investors and decision-makers to create opportunities where others see barriers.",
      cta: "Start a Confidential Conversation",
      scroll: "Scroll",
    },
    intro: {
      label: "Our Identity",
      heading: "We are not a consulting company. We are a Gateway.",
      body: "A Gateway opens doors. A Gateway connects people. A Gateway creates opportunities. Business is built on trust. Growth begins with the right connections.",
    },
    manifesto: {
      label: "Manifesto",
      items: [
        { t: "We create access.", d: "We do not sell standard consulting. We create opportunities and access to the right people, the right expertise, and the right network." },
        { t: "We connect.", d: "We bring together entrepreneurs, investors, experts and international partners. Our true value lies in our network." },
        { t: "We coordinate.", d: "We do not simply advise. We connect, coordinate and execute — discreetly and with international excellence." },
        { t: "We embody trust.", d: "Our product is trust and confidentiality. Our capital is our network. Our mission is to bring the right people together." },
      ],
    },
    marquee: ["Trust", "Access", "Network", "Discretion", "Opportunity", "International"],
    gateways: {
      label: "Global Gateways",
      heading: "International opportunities for entrepreneurs.",
      body: "Serbia is one of our first strategic hubs. From there we support entrepreneurs worldwide — with additional locations we add over time.",
      featured: "First hub",
      upcoming: "Coming soon",
      view: "View all gateways",
    },
    services: {
      label: "Services",
      heading: "Access to an exclusive international network.",
      body: "Our services are never standard packages. They are access. Our role is to coordinate, connect and guide.",
      list: [
        { t: "Business Relocation", d: "Relocation and international positioning." },
        { t: "Company Formation", d: "Formation and structuring of companies." },
        { t: "Banking & Financial Coordination", d: "Coordination of banks and financial partners." },
        { t: "Legal & Tax Coordination", d: "Legal and tax coordination." },
        { t: "Real Estate", d: "Access to premium real estate." },
        { t: "Investment Structuring", d: "Structuring of investments." },
        { t: "Strategic Partnerships", d: "Strategic partnerships and connections." },
        { t: "Executive Concierge", d: "Discreet service at the highest level." },
      ],
    },
    network: {
      label: "Network",
      heading: "Our capital is our network.",
      body: "We connect people. We connect ideas. We connect capital. We connect opportunities. When you enter our network, you speak with the people you want to speak with.",
      stats: [
        { n: "Confidential", l: "Every conversation" },
        { n: "International", l: "Without borders" },
        { n: "Curated", l: "The right connections" },
      ],
    },
    about: {
      label: "About SBG",
      heading: "SBG is an international platform.",
      body: "SBG stands for Salvatore Business Gateway — designed from day one as an international brand. Serbia is one of our first hubs, but our vision does not end at national borders.",
      p2: "When visitors leave our website, they should not think: “Interesting consulting company.” They should think: “These are people I want to speak with.”",
    },
    contact: {
      label: "Contact",
      heading: "Start a confidential conversation.",
      body: "The first contact should feel like the beginning of a long-term partnership, not the purchase of a product.",
      name: "Name", email: "Email", company: "Company", interest: "Area of interest", message: "Your message",
      submit: "Begin the conversation", sending: "Sending…",
      success: "Thank you. We will be in touch, discreetly.",
      error: "Something went wrong. Please try again.",
    },
    investment: {
      label: "Investment & Real Estate",
      heading: "Where strategy meets opportunity.",
      body: "Access to premium real estate and considered investment structures — coordinated, never promised.",
    },
    footer: { rights: "All rights reserved.", tagline: "Business Without Borders. Access Without Barriers." },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState("de");
  const toggle = useCallback(() => setLang((l) => (l === "de" ? "en" : "de")), []);
  const t = translations[lang];
  return <I18nContext.Provider value={{ lang, setLang, toggle, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
